<template>
  <div class="admin-layout-container">
    <!-- 侧边栏 -->
    <div class="sidebar" :class="{ 'collapsed': isSidebarCollapsed }">
      <div class="sidebar-header">
        <div class="logo">
          <!-- <img src="@/assets/logo.svg" alt="易甄学" /> -->
          <span v-show="!isSidebarCollapsed">易甄学管理系统</span>
        </div>
        <button class="sidebar-toggle" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </div>
      
      <div class="sidebar-body">
        <el-menu
          :default-active="activeMenu"
          class="sidebar-menu"
          :collapse="isSidebarCollapsed"
          background-color="var(--secondary-bg-color)"
          text-color="var(--text-color)"
          active-text-color="var(--primary-color)"
        >
          <!-- 使用循环渲染菜单项 -->
          <template v-for="item in adminMenuItems" :key="item.path">
            <!-- 没有子菜单的菜单项 -->
            <el-menu-item v-if="!item.children" :index="item.path" @click="navigateTo(item.path)">
              <el-icon v-if="item.icon"><component :is="item.icon" /></el-icon>
              <template #title>{{ item.title }}</template>
            </el-menu-item>
            
            <!-- 有子菜单的菜单项 -->
            <el-sub-menu v-else :index="item.path">
              <template #title>
                <el-icon v-if="item.icon"><component :is="item.icon" /></el-icon>
                <span>{{ item.title }}</span>
              </template>
              <!-- 子菜单项 -->
              <el-menu-item 
                v-for="child in item.children" 
                :key="child.path" 
                :index="child.path" 
                @click="navigateTo(child.path)"
              >
                {{ child.title }}
              </el-menu-item>
            </el-sub-menu>
          </template>
        </el-menu>
      </div>
    </div>

    <!-- 主内容区域 -->
    <div class="admin-main">
      <!-- 头部导航栏 -->
      <header class="admin-header">
        <div class="header-left">
          
          <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/admin/dashboard' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index">{{ item }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="header-right">
          <ThemeSwitcher class="theme-switcher mr-15" />
          <el-dropdown trigger="click">
            <div class="user-dropdown-link">
              <el-avatar :size="32" :src="userAvatar">{{ userInitials }}</el-avatar>
              <span class="username">{{ userName }}</span>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="navigateTo('/admin/profile')">个人中心</el-dropdown-item>
                <el-dropdown-item @click="navigateTo('/admin/settings')">设置</el-dropdown-item>
                <el-dropdown-item divided @click="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <!-- 内容区域 -->
      <main class="admin-content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import ThemeSwitcher from '@/components/ThemeSwitcher.vue'
import { 
  User as IconUser, 
  Odometer as IconDashboard, 
  Reading as IconReading,
  VideoPlay as IconVideoPlay,
  Files as IconFiles,
  Service as IconService,
  Wallet as IconMoneyCollect,
  Setting as IconSetting
} from '@element-plus/icons-vue'

// 定义菜单项类型
interface AdminMenuItem {
  path: string;
  title: string;
  icon?: any;
  children?: AdminMenuItem[];
}

const router = useRouter()
const route = useRoute()

// 侧边栏折叠状态
const isSidebarCollapsed = ref(localStorage.getItem('adminSidebarCollapsed') === 'true')

// 切换侧边栏折叠状态
const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  localStorage.setItem('adminSidebarCollapsed', isSidebarCollapsed.value.toString())
}

// 导航方法
const navigateTo = (path: string) => {
  router.push(path)
}

// 退出登录
const logout = () => {
  ElMessage.success('已退出登录')
  router.push('/login')
}

// 用户信息
const userName = ref('管理员')
const userAvatar = ref('')
const userInitials = computed(() => {
  return userName.value ? userName.value.charAt(0).toUpperCase() : ''
})

// 面包屑
const breadcrumbs = computed(() => {
  const paths = route.path.split('/').filter(Boolean)
  return paths.slice(1).map(path => {
    // 将路径转为更友好的显示名称
    switch (path) {
      case 'dashboard': return '总览'
     
      case 'user': return '用户管理'
      case 'list': return '用户列表'
      case 'roles': return '角色管理'
      case 'permissions': return '权限管理'

      case 'enrollment': return '招生管理'
      case 'plan': return '招生计划'
      case 'student': return '招生管理'
      case 'confirm': return '现场确认'

      case 'course': return '课程管理'
      case 'courselist': return '课程列表'
      case 'resource': return '课程资源'

      case 'question': return '题库管理'
      case 'bank': return '题目库'
      case 'paper': return '试卷库'
      case 'type': return '题型管理'

      case 'live': return '直播管理'
      case 'exam': return '考试管理'
      // case 'student': return '学籍管理'
      case 'finance': return '财务管理'
      case 'system': return '系统管理'
      case 'insight': return '洞察引擎'
      case 'aitools': return 'AI工具箱'
      
      case 'dynamic': return '动态考点趋势比对'
      case 'dataview': return '数据大屏'
      case 'category': return '分类'
     
      case 'schedule': return '排课'
      case 'playback': return '回放'
      // case 'paper': return '论文管理'
      case 'result': return '成绩'
      case 'class': return '班级'
      case 'order': return '订单'
      case 'payment': return '支付'
      case 'refund': return '退款'
      case 'settings': return '设置'
      case 'logs': return '日志'
      default: return path
    }
  })
})

// 当前激活的菜单
const activeMenu = computed(() => {
  return route.path
})

// 管理后台菜单项
const adminMenuItems: AdminMenuItem[] = [
  { 
    path: '/admin/dashboard', 
    title: '总览', 
    icon: IconDashboard 
  },
  {
    path: '/admin/user',
    title: '用户管理',
    icon: IconUser,
    children: [
      //学员列表
      { path: '/admin/user/studentlist', title: '学员列表' },
      //教师列表
      { path: '/admin/user/teacherlist', title: '教师列表' },
      //管理员列表
      { path: '/admin/user/adminlist', title: '管理员列表' },
      //部门管理
      { path: '/admin/user/department', title: '部门管理' },
      // //用户列表
      // { path: '/admin/user/list', title: '用户列表' },
      //角色管理
      { path: '/admin/user/roles', title: '角色管理' },
      //权限管理
      { path: '/admin/user/permissions', title: '权限管理' }
    ]
  },
  {
    path: '/admin/enrollment',
    title: '招生管理',
    icon: IconSetting,
    children: [
      { path: '/admin/enrollment/plan', title: '招生计划' },
      //招生管理
      { path: '/admin/enrollment/student', title: '报名管理' },
      //现场确认
      { path: '/admin/enrollment/confirm', title: '现场确认' },
    ]
  },
  //教学管理
  {
    path: '/admin/teaching',
    title: '教学管理',
    icon: IconSetting,
    children: [
      { path: '/admin/teaching/plan', title: '教学计划' },
      { path: '/admin/teaching/schedule', title: '排课管理' },  
    ]
  },
  //班级管理
  {
    path: '/admin/class',
    title: '班级管理',
    icon: IconSetting,
    children: [
      { path: '/admin/class/list', title: '班级列表' },
      //创建班级  
      { path: '/admin/class/create', title: '创建班级' },
      //班级学生
      { path: '/admin/class/student', title: '班级学生' },
      //班级分配
      { path: '/admin/class/assign', title: '班级分配' },
      //课程关联
      { path: '/admin/class/course', title: '课程关联' },
      //班级课表
      { path: '/admin/class/schedule', title: '班级课表' },
      //班级作业
      { path: '/admin/class/assignment', title: '班级作业' },
      //教学计划  
      { path: '/admin/class/plan', title: '教学计划' },
      //班级成绩  
      { path: '/admin/class/score', title: '班级成绩' },
      
    ]
  },
  //课程管理
  {
    path: '/admin/course',
    title: '课程管理',
    icon: IconReading,
    children: [
      { path: '/admin/course/courselist', title: '课程列表' },
      // { path: '/admin/course/category', title: '课程分类' },
      { path: '/admin/course/resource', title: '课程资源' }
    ]
  },
  //直播管理
  {
    path: '/admin/live',
    title: '直播管理',
    icon: IconSetting,
    children: [ 
    //直播课程
    { path: '/admin/live/course', title: '直播课程' },
    //直播设置
    { path: '/admin/live/settings', title: '直播间设置' },
    
    //直播统计
    { path: '/admin/live/statistics', title: '直播数据' },    
    ]
  },
  
  
  //题库管理，子菜单为题目库、试卷库、题型管理
  {
    path: '/admin/question',
    title: '题库管理',
    icon: IconSetting,
    children: [
      { path: '/admin/question/bank', title: '题目库' },
      { path: '/admin/question/paper', title: '试卷库' },
      // { path: '/admin/question/type', title: '题型管理' }
    ]
  },
  //考试管理,子菜单为考试安排，在线监考
  {
    path: '/admin/exam',
    title: '考试管理',
    icon: IconSetting,
    children: [
      { path: '/admin/exam/schedule', title: '考试安排' },
      { path: '/admin/exam/monitor', title: '在线监考' }
    ]

  },
  //论文管理，子菜单为论文审核，答辩管理，AI文献管理，专业翻译、写作指导、写作工具、排版导出
  {
    path: '/admin/paper',
    title: '论文管理',
    icon: IconSetting,
    children: [
      { path: '/admin/paper/audit', title: '论文审核' },
      { path: '/admin/paper/defense', title: '答辩管理' },
      { path: '/admin/paper/ai', title: 'AI文献管理' },
      { path: '/admin/paper/translate', title: '专业翻译' },
      { path: '/admin/paper/writing', title: '写作指导' },
      { path: '/admin/paper/tool', title: '写作工具' },   
      { path: '/admin/paper/export', title: '排版导出' }    
    ]
  },
  //洞察引擎https://bi.aliyuncs.com/token3rd/dashboard/view/pc.htm?pageId=a3722950-ad91-4805-b0f1-34f706f2cafd&accessTicket=221f517d-be7a-456a-9a30-91ab90841f71&dd_orientation=auto
  {
    path: '/admin/insight',
    title: '洞察引擎',
    icon: IconSetting,
    //https://bi.aliyuncs.com/token3rd/dashboard/view/pc.htm?pageId=a3722950-ad91-4805-b0f1-34f706f2cafd&accessTicket=221f517d-be7a-456a-9a30-91ab90841f71&dd_orientation=auto
  //直接
   
  },
  //动态考点趋势比对
  {
    path: '/admin/dynamic',
    title: '动态考点趋势比对',
    icon: IconSetting,
    
  },
  //数据大屏
  {
    path: '/admin/dataview',
    title: '数据大屏',
    icon: IconSetting,
    
  },

  //AI工具箱路由
  {
    path: '/admin/aitools',
    title: 'AI工具箱',
    icon: IconSetting,
    
    // children: [
    //   { path: '/admin/aitools/index', title: 'AI工具箱' },
    //   { path: '/admin/aitools/createlesson', title: '智能生成课程' },
    //   { path: '/admin/aitools/createtppt', title: '智能生成PPT' },
    //   { path: '/admin/aitools/createquestion', title: '智能生成题目' },
    //   //智能批改
    //   { path: '/admin/aitools/grader', title: '智能批改' },
    //   //智能改错
    // ]
  },
   {
    path: '/admin/system',
    title: '系统管理',
    icon: IconSetting,
    children: [
      { path: '/admin/system/settings', title: '系统设置' },
      { path: '/admin/system/logs', title: '操作日志' }
    ]
  }
  // 招生管理
  
  // {
  //   path: '/admin/live',
  //   title: '直播管理',
  //   icon: IconVideoPlay,
  //   children: [
  //     { path: '/admin/live/course', title: '直播课程' },
  //     { path: '/admin/live/schedule', title: '直播排课' },
  //     { path: '/admin/live/playback', title: '回放管理' }
  //   ]
  // },
  // {
  //   path: '/admin/exam',
  //   title: '考试管理',
  //   icon: IconFiles,
  //   children: [
  //     { path: '/admin/exam/list', title: '考试列表' },
  //     { path: '/admin/exam/question', title: '题库管理' },
  //     { path: '/admin/exam/result', title: '成绩管理' }
  //   ]
  // },
  // {
  //   path: '/admin/student',
  //   title: '学籍管理',
  //   icon: IconService,
  //   children: [
  //     { path: '/admin/student/list', title: '学籍信息' },
  //     { path: '/admin/student/class', title: '班级管理' }
  //   ]
  // },
  // {
  //   path: '/admin/finance',
  //   title: '财务管理',
  //   icon: IconMoneyCollect,
  //   children: [
  //     { path: '/admin/finance/order', title: '订单管理' },
  //     { path: '/admin/finance/payment', title: '支付管理' },
  //     { path: '/admin/finance/refund', title: '退款管理' }
  //   ]
  // },
 
]
</script>

<style lang="scss" scoped>
.admin-layout-container {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: var(--background-color, #f5f7fa);
  color: var(--text-color, #333333);
}

.sidebar {
  display: flex;
  flex-direction: column;
  width: 240px;
  height: 100%;
  background-color: var(--secondary-bg-color, #fff);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  transition: width 0.3s;
  
  &.collapsed {
    width: 64px;
    
    .logo span {
      display: none;
    }
  }
  
  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    padding: 0 15px;
    border-bottom: 1px solid var(--border-color, #eaeaea);
    
    .logo {
      display: flex;
      align-items: center;
      
      img {
        width: 32px;
        height: 32px;
        margin-right: 10px;
      }
      
      span {
        font-size: 16px;
        font-weight: 600;
        color: var(--primary-color, #0e6736);
        white-space: nowrap;
      }
    }
    
    .sidebar-toggle {
      background: none;
      border: none;
      color: var(--text-color, #333);
      cursor: pointer;
      font-size: 16px;
      
      &:hover {
        color: var(--primary-color, #0e6736);
      }
    }
  }
  
  .sidebar-body {
    flex: 1;
    overflow-y: auto;
    
    .sidebar-menu {
      border-right: none;
    }
    
    :deep(.el-menu-item.is-active) {
      background-color: #f5f7fa !important;
    }
    
    :deep(.el-menu-item:hover) {
      background-color: #f5f7fa !important;
    }
    
    :deep(.el-sub-menu__title:hover) {
      background-color: #f5f7fa !important;
    }
  }
}

.admin-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 20px;
  background-color: var(--secondary-bg-color, #fff);
  border-bottom: 1px solid var(--border-color, #eaeaea);
  
  .header-left {
    display: flex;
    align-items: center;
  }
  
  .header-right {
    display: flex;
    align-items: center;
    
    .user-dropdown-link {
      display: flex;
      align-items: center;
      cursor: pointer;
      
      .username {
        margin-left: 8px;
        font-size: 14px;
      }
    }
  }
}

.admin-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: var(--background-color, #f5f7fa);
}

.mr-15 {
  margin-right: 15px;
}
</style> 